import { Navigate, useLocation, type Location } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserContext } from "@/context/UserContext";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {
  hasClientCreatedProfile,
  isClientOnboardingComplete,
  type UserWithCompany,
} from "@/shared/constants/clientOnboarding.const";

type DecodedToken = {
  id: string;
  email: string;
  role: "ADMIN" | "CLIENT";
  exp: number;
  iat: number;
};
const PROFILE_VERIFICATION_LANDING_KEY = "gt:profileVerificationSummary";

/** No hooks — safe to return early on every branch (avoids Rules of Hooks issues with redirects). */
function ProtectedRouteGate({
  isAuthenticated,
  isLoading,
  userDetails,
  location,
  children,
}: {
  isAuthenticated: boolean;
  isLoading: boolean;
  userDetails: UserWithCompany | null | undefined;
  location: Location;
  children: React.ReactNode;
}) {
  const token = Cookies.get("token");
  if (token) {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      if (decoded.role === "ADMIN") {
        return <Navigate to="/admin/companies" replace />;
      }
    } catch {
      // Invalid token, continue with normal flow
    }
  }

  if (!isAuthenticated && !isLoading) {
    return <Navigate to="/login" replace />;
  }

  if (isLoading || userDetails === undefined) {
    return <div>Loading...</div>;
  }

  if (userDetails === null) {
    return <Navigate to="/login" replace />;
  }

  const isComplete = isClientOnboardingComplete(userDetails);
  const status = userDetails.company?.status;
  const path = location.pathname;
  const profileCreated = hasClientCreatedProfile(userDetails);

  // Profile builder is one-time onboarding — never return completed users here.
  if (profileCreated && path === "/create-profile") {
    return <Navigate to="/my-discovery" replace />;
  }

  if (!isComplete) {
    if (!profileCreated) {
      if (path !== "/create-profile") {
        return <Navigate to="/create-profile" replace />;
      }
      return <>{children}</>;
    }

    if (status === "UNDER_REVIEW" && path === "/profile/edit") {
      sessionStorage.setItem(
        PROFILE_VERIFICATION_LANDING_KEY,
        JSON.stringify({ kind: "EDIT_LOCKED_UNDER_REVIEW" })
      );
      return <Navigate to="/my-discovery" replace />;
    }

    // Onboarding "incomplete" means the company isn't verified yet, but the user should still be able to
    // access their own account areas (profile/settings/credits/edit profile) without being trapped on
    // the discovery page.
    const allowWhileUnverified =
      path === "/my-discovery" ||
      path.startsWith("/my-discovery/") ||
      path === "/profile" ||
      path === "/profile/edit" ||
      path === "/settings" ||
      path === "/credits" ||
      path.startsWith("/credits/") ||
      path === "/invoices" ||
      path === "/invoices/wallet" ||
      path === "/two-factor/setup" ||
      path === "/dashboard";

    if (!allowWhileUnverified) {
      return <Navigate to="/my-discovery" replace />;
    }
  }

  return <>{children}</>;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { userDetails } = useUserContext();
  const location = useLocation();

  return (
    <ProtectedRouteGate
      isAuthenticated={isAuthenticated}
      isLoading={isLoading}
      userDetails={userDetails as UserWithCompany | null | undefined}
      location={location}
    >
      {children}
    </ProtectedRouteGate>
  );
}
